import { useQuery } from '@tanstack/react-query';
import { BracketData, TournamentParticipant } from '@types';
import { AxiosContext } from '@/context/AxiosContext';
import { useContext } from 'react';

const normalizeParticipant = (
  participant?: TournamentParticipant | null
): TournamentParticipant | null => {
  if (!participant) {
    return null;
  }

  return {
    ...participant,
    tournamentWins: participant.tournamentWins ?? 0,
    tournamentTitle: participant.tournamentTitle ?? '',
    tournamentBadgeKey: participant.tournamentBadgeKey ?? '',
  };
};

/**
 * Hook to fetch tournament bracket visualization data
 */
export const useTournamentBracket = (tournamentId: string | null) => {
  const axios = useContext(AxiosContext);

  return useQuery<BracketData>({
    queryKey: ['tournament', tournamentId, 'bracket'],
    queryFn: async () => {
      if (!tournamentId) throw new Error('Tournament ID is required');
      const response = await axios.get<BracketData>(`/api/challenges/Tournament/${tournamentId}/bracket`);
      return {
        ...response.data,
        winner: normalizeParticipant(response.data.winner),
        placements: response.data.placements
          ? {
              first: normalizeParticipant(response.data.placements.first),
              second: normalizeParticipant(response.data.placements.second),
              third: normalizeParticipant(response.data.placements.third),
            }
          : response.data.placements,
        rounds: response.data.rounds.map((round) => ({
          ...round,
          matches: round.matches.map((match) => ({
            ...match,
            participant1: normalizeParticipant(match.participant1),
            participant2: normalizeParticipant(match.participant2),
            winner: normalizeParticipant(match.winner),
            loser: normalizeParticipant(match.loser),
          })),
        })),
        thirdPlaceRound: response.data.thirdPlaceRound
          ? {
              ...response.data.thirdPlaceRound,
              matches: response.data.thirdPlaceRound.matches.map((match) => ({
                ...match,
                participant1: normalizeParticipant(match.participant1),
                participant2: normalizeParticipant(match.participant2),
                winner: normalizeParticipant(match.winner),
                loser: normalizeParticipant(match.loser),
              })),
            }
          : response.data.thirdPlaceRound,
      };
    },
    enabled: !!tournamentId,
    refetchInterval: 30000, // Refresh every 30 seconds to get live updates
  });
};

export default useTournamentBracket;
